import type { MouseEvent } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Phone, UserPlus } from "lucide-react";
import type { Admin, OutreachProspect } from "@lumika/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  cn,
} from "@lumika/ui";
import { formatDateTime } from "../outreach-ui";
import { safePhoneHref } from "./board-utils";

const UNASSIGNED = "__unassigned__";

type Props = {
  prospect: OutreachProspect;
  ownerName: string | null;
  admins: Admin[];
  onClick: (prospect: OutreachProspect) => void;
  onAssign: (prospectId: string, ownerAdminId: string | null) => void;
};

/**
 * The visible face of a ticket — dealer name, contact, phone, last-updated.
 * Shared by the live draggable card and the drag overlay so the thing you drag
 * looks exactly like the thing you picked up.
 */
function ProspectCardBody({ prospect }: { prospect: OutreachProspect }) {
  const phoneHref = safePhoneHref(prospect.phone);
  return (
    <>
      <p className="truncate text-sm font-semibold leading-tight">{prospect.companyName}</p>

      {prospect.contactName && (
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{prospect.contactName}</p>
      )}

      {phoneHref && (
        <a
          href={phoneHref}
          className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Phone className="h-3 w-3 shrink-0" />
          <span className="truncate">{prospect.phone}</span>
        </a>
      )}

      {prospect.tags.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {prospect.tags.map((tag) => (
            <span
              key={tag}
              className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <p className="mt-1.5 text-[11px] text-muted-foreground/80">
        Updated {formatDateTime(prospect.updatedAt)}
      </p>
    </>
  );
}

/**
 * Lifted clone shown under the pointer while dragging. Rendered in a portal via
 * dnd-kit's DragOverlay so it floats above the columns instead of being clipped
 * by their scroll containers.
 */
export function ProspectCardOverlay({ prospect }: { prospect: OutreachProspect }) {
  return (
    // Purely visual clone; the real card keeps the label, so hide this from AT.
    <article
      aria-hidden="true"
      className="w-60 cursor-grabbing rounded-lg border border-primary/30 bg-card p-3 shadow-xl ring-2 ring-primary/15 md:w-[17rem]"
    >
      <ProspectCardBody prospect={prospect} />
    </article>
  );
}

/**
 * Draggable kanban "ticket". Click opens the slide-over panel; a pointer drag of
 * ≥5px or a touch dwell ≥200ms moves it between columns. While dragging, the
 * real card dims and a DragOverlay clone follows the pointer.
 */
export function ProspectCard({ prospect, ownerName, admins, onClick, onAssign }: Props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: prospect.id,
  });

  function handleClick(e: MouseEvent) {
    // Only open the panel on a plain click (not during/after a drag).
    if (!isDragging) onClick(prospect);
    e.stopPropagation();
  }

  function handleAssignChange(value: string) {
    onAssign(prospect.id, value === UNASSIGNED ? null : value);
  }

  return (
    <article
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      className={cn(
        "cursor-grab select-none rounded-lg border bg-card p-3 shadow-sm",
        "transition-[box-shadow,opacity] hover:border-foreground/15 hover:shadow-md active:cursor-grabbing",
        // Leave a ghost in place while the DragOverlay clone follows the pointer.
        isDragging && "opacity-40",
      )}
      aria-label={`${prospect.companyName} — click to open details`}
    >
      <ProspectCardBody prospect={prospect} />

      {/* Inline owner assignment — isolated from drag/click on the card */}
      <div
        className="mt-2"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Select value={prospect.ownerAdminId ?? UNASSIGNED} onValueChange={handleAssignChange}>
          <SelectTrigger
            className="h-7 w-full px-2 text-xs"
            aria-label={ownerName ? `Owner: ${ownerName}` : "Assign owner"}
          >
            {ownerName ? (
              <span className="truncate">{ownerName}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <UserPlus className="h-3 w-3 shrink-0" />
                Assign
              </span>
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {admins.map((admin) => (
              <SelectItem key={admin.id} value={admin.id}>
                {admin.displayName ?? admin.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </article>
  );
}
